import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import SuggestionsWrapper from '@utilities/components/MentionsInput/components/SuggestionsWrapper';
import classNames from 'classnames';

import styles from './_index.module.scss';

type MentionItem = {
  id: string;
  label: string;
  trigger: string;
};

type MentionListProps = {
  command?: (item: MentionItem) => void;
  items?: MentionItem[];
  selectedIndex?: number;
  suggestions?: Record<string, unknown>;
};

export type MentionListRef = {
  onKeyDown: ({ event }: { event: KeyboardEvent }) => boolean;
};

const MentionList = forwardRef<MentionListRef, MentionListProps>((props, ref) => {
  const { command, items = [], selectedIndex = 0, suggestions = {} } = props;

  const [activeIndex, setActiveIndex] = useState(selectedIndex);

  useEffect(() => {
    if (!items.length) {
      setActiveIndex(0);
      return;
    }

    setActiveIndex((currentIndex) => {
      const preferredIndex = Number.isInteger(selectedIndex) ? selectedIndex : currentIndex;
      return Math.max(0, Math.min(preferredIndex, items.length - 1));
    });
  }, [items, selectedIndex]);

  const selectItem = (index: number) => {
    const item = items[index];
    if (!item) return;
    command?.(item);
  };

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (!items.length) return false;

        if (event.key === 'ArrowUp') {
          event.preventDefault();
          setActiveIndex((previousIndex) => (previousIndex + items.length - 1) % items.length);
          return true;
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setActiveIndex((previousIndex) => (previousIndex + 1) % items.length);
          return true;
        }

        if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault();
          selectItem(activeIndex);
          return true;
        }

        return false;
      },
    }),
    [activeIndex, items]
  );

  return (
    <SuggestionsWrapper {...suggestions}>
      <div className={styles['mention-list']}>
        {items.map((item, index) => (
          <button
            className={classNames(styles['mention-list-item'], {
              [styles['is-selected']]: index === activeIndex,
            })}
            key={`${item.trigger}-${item.id}`}
            onMouseDown={(event) => {
              event.preventDefault();
              command?.(item);
            }}
            onMouseEnter={() => setActiveIndex(index)}
            type="button"
          >
            {item.label}
          </button>
        ))}
      </div>
    </SuggestionsWrapper>
  );
});

MentionList.displayName = 'MentionList';

export default MentionList;
